import Vue from "vue";
import axios from "axios";
import {reactive, toRefs, computed, provide, inject, isRef} from "@vue/composition-api";
import {state as accountState} from "./account";
import {state as appState} from "./app";
import {listStateMerge, updateListState} from "../store_helpers";
import {$t} from "@/i18n";

export const state = reactive({
    map: new Map(), list: [], fetched_account_id: null,

    // available custom field types
    field_types: [
        // all
        {type: "text", text: $t("Text"), related_type: null},
        {type: "number", text: $t("Number"), related_type: null},
        {type: "choice_single", text: $t("Choice"), related_type: null},

        // contact, client, supplier, project
        {type: "boolean", text: $t("Yes/No"), related_type: ["client", "contact", "supplier", "project"]},
        {type: "integer", text: $t("Integer"), related_type: ["client", "contact", "supplier", "project"]},
        {type: "date", text: $t("Date"), related_type: ["client", "contact", "supplier", "project"]},
        {type: "choice_multi", text: $t("Multiple choice"), related_type: ["client", "contact", "supplier", "project"]},
        {type: "url", text: $t("URL"), related_type: ["client", "contact", "supplier", "project"]},

        // client, supplier, project
        {type: "user_single", text: $t("User"), related_type: ["client", "supplier", "project"]},
        {type: "user_multi", text: $t("Multiple users"), related_type: ["client", "supplier", "project"]},
        {type: "note", text: $t("Multiline text"), related_type: ["client", "supplier", "project"]},

        // task
        {type: "article", text: $t("Article"), related_type: ["task"]},
        {type: "price", text: $t("Price"), related_type: ["task"], feature: "billing"},
    ],
});

export const actions = {
    fetchCustomFields(account_id, related_type = 'task', default_fields_only = 0, force = false) {
        //todo: is this function needed any more
        return new Promise(function (resolve, reject) {
            if (!account_id) account_id = accountState.current_account.id;

            if (!state.fetched_account_id || state.fetched_account_id !== account_id || force) {
                state.fetched_account_id = account_id;

                let params = {related_type, default_fields_only};
                axios
                    .get(Vue.config.url_prefix + "/api/a/{0}/customfields/".format(account_id), {params: params})
                    .then(function (resp) {
                        state.map = new Map(resp.data.map(i => [i.id, i]));
                        state.list = listStateMerge(state.list, resp.data, "account_id", account_id, "id");
                        resolve(resp.data);
                    })
                    .catch(function (ex) {
                        reject(ex);
                    });
            } else {
                resolve();
            }
        });
    }, fetchCustomField({account_id, custom_field_id}) {
        return new Promise((resolve, reject) => {
            axios
                .get(Vue.config.url_prefix + "/api/a/{0}/customfields/{1}".format(account_id, custom_field_id))
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    }, saveCustomField(data, related_type) {
        return new Promise(function (resolve, reject) {
            const params = {
                related_type
            }

            let id = data.id ? data.id : "";
            axios
                .post(Vue.config.url_prefix + "/api/a/{0}/customfields/{1}".format(data.account_id, id), data, {params})
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    }, deleteCustomField(account_id, custom_field_id, raise_error = 1) {
        return new Promise(function (resolve, reject) {
            state.fetched_account_id = null;
            axios
                .delete(Vue.config.url_prefix + "/api/a/{0}/customfields/{1}".format(account_id, custom_field_id), {params: {raise_error}})
                .then(function () {
                    updateListState(custom_field_id, null, state.list);
                    resolve();
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    }, saveCustomFields(data_list, related_type) {
        return new Promise(function (resolve, reject) {
            const params = {
                related_type
            }
            state.fetched_account_id = null;
            if (data_list.length === 0) {
                resolve();
                return;
            }

            const account_id = data_list[0].account_id;

            axios
                .post(Vue.config.url_prefix + "/api/a/{0}/customfields/".format(account_id), data_list, {params})
                .then(function (resp) {
                    resolve(resp.data);
                })
                .catch(function (ex) {
                    reject(ex);
                });
        });
    }
};

export const getters = {
    custom_fields: computed(() => state.list),

    getCustomFields(account_id, related_type) {
        if (appState.custom_fields[account_id] === undefined) return [];
        let fields = appState.custom_fields[account_id].filter(x => x.related_type === related_type);
        if (fields.length === 0) return [];
        return fields;
    },

    getGroupedCustomFields(accountId, relatedType, relatedObject, excludedFieldNames) {

        if (appState.custom_fields[accountId] === undefined) return []
        let customFields = appState.custom_fields[accountId].filter(x => x.related_type === relatedType)
        if (!customFields.length) return []

        if (!excludedFieldNames) excludedFieldNames = []
        let objectCustomFields = relatedObject.fields ? Vue.clone_object(relatedObject.fields) : {}
        // console.log('objectCustomFields', objectCustomFields)

        for (let i = 0; i < customFields.length; i++) {
            let field = customFields[i];
            objectCustomFields[field.name + "_error"] = null;

            let isTableField = field.data_column !== "{0}.fields".format(relatedType)
            if (isTableField) {
                objectCustomFields[field.name] = relatedObject[field.name]
            }
            if (field.type === 'contacts' && !field.group_name) {
                field.group_name = $t('Contacts')
            }
            // FIXME :: this needs an actual solution, this is temp fix
            else if (field.group_name === 'Digitisation of client documents') {
                field.group_name = $t('Digitisation of client documents')
            } else if (field.group_name === 'Client portal') {
                field.group_name = $t('Client portal')
            }
            // console.log('fld.name', field.name + " -> " + relatedObject[field.name])
        }

        let sortedCustomFields = Array.sort_by(customFields, "sort_order")
        // console.log('sortedCustomFields', sortedCustomFields)
        let groupNames = sortedCustomFields.filter(x => excludedFieldNames.indexOf(x.data_column) === -1).map(x => (x.group_name == null) ? "" : x.group_name)
        // console.log('groupNames', groupNames)

        return Array.unique(groupNames).map(groupName => {
            let groupFields = customFields.filter(y => {
                let isSameGroup = String.str_equal(y.group_name == null ? "" : y.group_name, groupName, true)
                return isSameGroup && excludedFieldNames.indexOf(y.data_column) === -1
            })
            let resultFields = groupFields.map(field => {
                return {name: field.name, field: field, value: objectCustomFields[field.name]}
            })
            let sortedFields = resultFields.sort((a, b) => {
                if (a.field.sort_order < b.field.sort_order) return -1
                if (a.field.sort_order > b.field.sort_order) return 1
                return 0
            })
            return {group_name: groupName, fields: sortedFields}
        })
    },

    account_custom_fields: account_id => computed(() => {
        const acc_id = isRef(account_id) ? account_id.value : account_id;
        return state.list.filter(cf => Number(cf.account_id) === Number(acc_id));
    }),

    account_custom_monitoring_fields: account_id => computed(() => {
        const _company_id = isRef(account_id) ? account_id.value : account_id;
        return state.list.filter(cf => Number(cf.account_id) === Number(_company_id) && cf.is_monitor === 1);
    }),

    getAccountCalculateByTypes: account_id => computed(() => {
        const default_calculate_per_types = [
            {value: "fixed_price", text: $t("Fixed price")},
            {value: "total_hours", text: $t("Total hours")},
            {value: "normal_hours", text: $t("Normal hours")},
            {value: "extra_hours", text: $t("Extra hours")}
        ];
        const account_custom_fields_with_type_number = getters.custom_fields.value.filter(cf => (cf.account_id === account_id && ["number", "price"].includes(cf.type))).map(cf => {
            // only numeric fields allowed to use in product calculations
            const value = "custom_field/" + cf.name;
            const text = $t("Field: ") + cf.title;
            return {...cf, value, text};
        });
        return [...default_calculate_per_types, ...account_custom_fields_with_type_number];
    }),

};

export const key = Symbol("CustomFieldsModule");

export const provideCustomFields = () => {
    provide(key, {
        ...toRefs(state), ...getters, ...actions
    });
};

export default () => inject(key);
